import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"
import { Loader2 } from "lucide-react"

import { useUpdateLease } from "@/features/leases/hooks/useLeases"
import { Button } from "@/components/ui/button"
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { CURRENCIES } from "@/lib/constants"
import type { Lease } from "@rentbase/shared"

const leaseSchema = z.object({
    start_date: z.string().min(1, "Start date is required"),
    end_date: z.string().optional(),
    monthly_rent: z.coerce.number().min(0, "Rent must be 0 or more"),
    currency: z.string().min(1, "Currency is required"),
    payment_due_day: z.coerce.number().int().min(1).max(28).default(1),
    deposit_amount: z.coerce.number().optional(),
    lease_type: z.string().optional(),
    notes: z.string().optional(),
})

type LeaseFormValues = z.infer<typeof leaseSchema>

export function LeaseForm({
    lease,
    open,
    onOpenChange,
}: {
    lease: Lease
    open: boolean
    onOpenChange: (v: boolean) => void
}) {
    const updateLease = useUpdateLease()

    const form = useForm<LeaseFormValues>({
        resolver: zodResolver(leaseSchema),
        defaultValues: {
            start_date: "",
            end_date: "",
            monthly_rent: 0,
            currency: "EUR",
            payment_due_day: 1,
            deposit_amount: undefined,
            lease_type: "",
            notes: "",
        },
    })

    useEffect(() => {
        if (lease && open) {
            form.reset({
                start_date: lease.start_date ? new Date(lease.start_date).toISOString().split("T")[0] : "",
                end_date: lease.end_date ? new Date(lease.end_date).toISOString().split("T")[0] : "",
                monthly_rent: lease.monthly_rent != null ? Number(lease.monthly_rent) : 0,
                currency: lease.currency || "EUR",
                payment_due_day: (lease as any).payment_due_day != null ? Number((lease as any).payment_due_day) : 1,
                deposit_amount: (lease as any).deposit_amount != null ? Number((lease as any).deposit_amount) : undefined,
                lease_type: lease.lease_type || "",
                notes: lease.notes || "",
            })
        }
    }, [lease, open, form])

    function onSubmit(values: LeaseFormValues) {
        const payload = Object.fromEntries(
            Object.entries(values).filter(([, v]) => v !== "" && v !== undefined)
        )

        updateLease.mutate(
            { id: lease.id, input: payload },
            {
                onSuccess: () => {
                    toast.success("Lease updated successfully")
                    onOpenChange(false)
                },
                onError: (err: unknown) => {
                    toast.error(err instanceof Error ? err.message : "Failed to update lease")
                },
            }
        )
    }

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="bottom" className="h-[85dvh] overflow-y-auto rounded-t-2xl">
                <SheetHeader className="mb-4">
                    <SheetTitle>Edit Lease</SheetTitle>
                </SheetHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
                        <div className="grid grid-cols-2 gap-3">
                            <FormField
                                control={form.control}
                                name="start_date"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Start Date *</FormLabel>
                                        <FormControl>
                                            <Input type="date" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="end_date"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>End Date</FormLabel>
                                        <FormControl>
                                            <Input type="date" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <FormField
                                control={form.control}
                                name="monthly_rent"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Monthly Rent *</FormLabel>
                                        <FormControl>
                                            <Input type="number" min={0} step="0.01" placeholder="0" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="currency"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Currency</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Currency" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {CURRENCIES.map((c) => (
                                                    <SelectItem key={c.code} value={c.code}>
                                                        {c.code}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="payment_due_day"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Rent Due Day</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            min={1}
                                            max={28}
                                            placeholder="1"
                                            {...field}
                                        />
                                    </FormControl>
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Day of each month when rent is due (1–28)
                                    </p>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="deposit_amount"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Deposit</FormLabel>
                                    <FormControl>
                                        <Input type="number" min={0} step="0.01" placeholder="0" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="lease_type"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Lease Type</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a type" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="fixed">Fixed Term</SelectItem>
                                            <SelectItem value="rolling">Rolling / Periodic</SelectItem>
                                            <SelectItem value="short">Short Let</SelectItem>
                                            <SelectItem value="student">Student</SelectItem>
                                            <SelectItem value="seasonal">Seasonal</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="notes"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Notes</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            placeholder="Any specific terms or notes about this lease..."
                                            className="resize-none"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <Button
                            type="submit"
                            className="w-full h-12 rounded-xl mt-4"
                            disabled={updateLease.isPending}
                        >
                            {updateLease.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                "Save Changes"
                            )}
                        </Button>
                    </form>
                </Form>
            </SheetContent>
        </Sheet>
    )
}
